import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { useLive, useNow, useRuntime } from './context';
import { useHasModule, useRemote } from './useRemote';
import { ApiFailure } from '../api/errors';
import { addDays, formatClock, formatFreshness, formatMoney, isoDayIn } from '../mirror/time';
import {
  MenuSoldOutResult,
  Order,
  OrderList,
  OrderStatus,
  OrderTransitionDetail,
  StaffMenu,
} from '../wire';
import type { Order as OrderType, StaffMenuItem } from '../wire';
import { useLocale, useT } from '../messages';
import type { Messages, T, UiLocale } from '../messages';

/**
 * **Orders** — the kitchen pass and the counter (spec §3.6).
 *
 * §3.6 is blunt about why this screen exists at all: *"An API that accepts
 * orders nobody notices is worse than no API."* This is the noticing.
 *
 * ## Online, and stated as such
 *
 * Nothing here touches the mirror or the outbox. The argument is in
 * `useRemote.ts` and the short version is that an order lane is a race with
 * other people — a cached "ready" is not a stale answer, it is a wrong one — and
 * that the buttons themselves come from the server. A pass hangs on a wall with
 * a router under it; if the router is gone the honest screen says when it last
 * read, which is what the line under the heading does.
 *
 * ## The buttons are the server's
 *
 * Every action drawn below comes from `next_statuses` on the row, which is
 * `ORDER_TRANSITIONS` projected by `orderToWire`. There is **no transition table
 * in this file** and there must not be: the domain owns which moves are legal,
 * a second copy on a tablet is a second answer, and the tablet's copy is the one
 * that is out of date. `orders.ts` says the same thing from its side — the
 * refusal "carries the list of moves that *are* legal, so a kitchen tablet
 * holding a stale order can redraw its buttons from the error instead of
 * guessing."
 *
 * So when a `409` comes back, this screen does exactly that: it shows the
 * server's sentence **verbatim**, redraws that ticket's buttons from
 * `detail.allowed`, and re-reads the day.
 *
 * ## Placing an order is not here
 *
 * Counter orders arrive by their own flow later. This is the working screen —
 * the one that turns a placed order into food that leaves the building.
 *
 * ## The kitchen's own controls (`docs/verticals-plan.md` V1)
 *
 * Two things spec §3.6 promised out loud and this screen now keeps:
 *
 *  - **Confirm takes a prep time.** A `placed` ticket's Confirm button sits
 *    beside a minutes box prefilled from the house default (`GET /v1/menu`'s
 *    `policy.default_prep_minutes`), adjustable, and one tap sends both. An
 *    empty box sends no number, and the server writes the same default — so
 *    an untouched box and an absent field mean one thing, and the screen holds
 *    no second copy of the rule. The text to the guest ("ready about 6:20") is
 *    the server's to enqueue, from that same move; nothing here composes it.
 *  - **The 86 board.** The whole menu with its `sold_out` flags, per dish and
 *    per choice, one tap each — drawn from the same projection the guest's
 *    menu and the partner's menu are built from, so what the kitchen sees out
 *    is what the guest is told is out. The buttons are drawn only for a seat
 *    that may write (`session.can_write`, the same gate `Team.tsx` uses):
 *    `core-api` answers `403` to a readonly seat on the verbs, and a button
 *    that opens a door that does not open is the thing this screen is most
 *    careful not to draw.
 */

/**
 * The lanes, left to right.
 *
 * This is a **grouping of the status vocabulary**, not a claim about which moves
 * are legal — the two happen to read in the same order because the lifecycle is
 * linear, and if a sixth status ever appeared it would arrive here as a column
 * and in `next_statuses` as a button, independently. `cancelled` is not a lane:
 * it is shown below, muted, because §3.6's cancelled order is a thing that
 * happened rather than a thing to work on.
 */
const LANES = [
  { status: 'placed', label: 'orders.lanePlaced' },
  { status: 'confirmed', label: 'orders.laneConfirmed' },
  { status: 'preparing', label: 'orders.lanePreparing' },
  { status: 'ready', label: 'orders.laneReady' },
  { status: 'completed', label: 'orders.laneCompleted' },
] as const satisfies readonly { status: OrderStatus; label: keyof Messages }[];

/**
 * The verbs, **translated at render** — the row is a vocabulary and the language
 * is decided by whoever is standing at the pass.
 */
const STATUS_VERB = {
  placed: 'orders.verbPlaced',
  confirmed: 'orders.verbConfirmed',
  preparing: 'orders.verbPreparing',
  ready: 'orders.verbReady',
  completed: 'orders.verbCompleted',
  cancelled: 'orders.verbCancelled',
} as const satisfies Record<OrderStatus, keyof Messages>;

/**
 * One ticket's outstanding refusal: what the server said, what it allows, and
 * what it says the order actually *is*.
 *
 * The third field is what makes the refusal self-clearing. It stands only while
 * the row on screen still disagrees with the server; the moment the re-read
 * brings back a row in `serverStatus`, `next_statuses` is authoritative again
 * and the refusal has nothing left to correct.
 */
interface Refusal {
  orderId: string;
  message: string;
  /** `null` when the refusal carried no list — a race rather than a bad move. */
  allowed: OrderStatus[] | null;
  serverStatus: OrderStatus | null;
}

export function Orders(): ReactNode {
  const runtime = useRuntime();
  useNow();
  const now = runtime.now();
  const t = useT();
  const locale = useLocale();

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';
  const today = isoDayIn(timezone, now);

  const [picked, setPicked] = useState<string | null>(null);
  const day = picked ?? today;
  const [openTicket, setOpenTicket] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /**
   * The minutes box per placed ticket, as typed. Absent means "untouched" and
   * the box shows the house default; `''` means the person cleared it, which
   * sends no number — and the server writes the same default either way.
   */
  const [prep, setPrep] = useState<Record<string, string>>({});
  const [boardOpen, setBoardOpen] = useState(false);
  const [flipping, setFlipping] = useState<string | null>(null);
  const [boardError, setBoardError] = useState<string | null>(null);
  const mayWrite = session?.can_write === true;

  /**
   * The gate, twice.
   *
   * `App` does not draw the tab at all when `ordering` is off — that is the
   * hiding §3.2 asks for. This second check is what stops the screen *asking*:
   * a module that is off answers `404` on every read, and a screen that polls
   * one every fifteen seconds would be filling a log with the tenant's absence.
   */
  const entitled = useHasModule('ordering');

  const orders = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/orders',
        query: { date_from: day, date_to: day, limit: 200 },
        schema: OrderList,
        signal,
      }),
    [day],
    {
      // The stream carries `order.created`, `order.updated` and `order.ready`
      // (`repos/orders.ts` emits all three), so a ticket appears about a second
      // after the till takes it. The poll is the backstop for a dead stream.
      cue: (type) => type.startsWith('order.'),
      pollMs: 15_000,
      enabled: entitled,
    },
  );

  /**
   * The board and the house policy, read once a minute rather than with the
   * pass: an 86 is a fact about *now*, but a menu is a few hundred rows and
   * the stream carries no `menu.*` cue to re-read on. A tap on a flag re-reads
   * at once; the poll is for the other tablet's tap.
   */
  const menu = useRemote(
    (signal) => runtime.api.request({ method: 'GET', path: '/menu', schema: StaffMenu, signal }),
    [],
    { pollMs: 60_000, enabled: entitled },
  );
  const defaultPrep = menu.data?.policy.default_prep_minutes ?? null;

  const rows = orders.data?.orders ?? [];
  const live = rows.filter((order) => order.status !== 'cancelled');
  const cancelled = rows.filter((order) => order.status === 'cancelled');
  const ticket = rows.find((order) => order.order_id === openTicket) ?? null;

  /** What the minutes box shows for one ticket: typed, else the house default. */
  const prepFor = (orderId: string): string =>
    prep[orderId] ?? (defaultPrep === null ? '' : String(defaultPrep));

  const move = useCallback(
    async (order: OrderType, status: OrderStatus): Promise<void> => {
      setBusy(order.order_id);
      setRefusal(null);
      // The minutes ride only the move to `confirmed` (0057), and only when
      // the box holds a whole number. Anything else sends nothing, and the
      // server writes the house default — one rule, in one place.
      const typed = prep[order.order_id];
      const minutes =
        status !== 'confirmed'
          ? undefined
          : typed === undefined
            ? (defaultPrep ?? undefined)
            : /^\d{1,3}$/.test(typed.trim())
              ? Number(typed.trim())
              : undefined;
      try {
        await runtime.api.request({
          method: 'POST',
          path: `/orders/${order.order_id}/status`,
          body: {
            status,
            ...(minutes === undefined ? {} : { prep_minutes: minutes }),
            ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
          },
          // A pass gets tapped twice by two people. The memo in `intents.ts`
          // makes the second tap the first answer rather than a second move.
          idempotencyKey: ulid(),
          schema: Order,
        });
      } catch (e) {
        if (e instanceof ApiFailure && e.status === 409) {
          const detail = OrderTransitionDetail.safeParse(e.detail);
          setRefusal({
            orderId: order.order_id,
            // Verbatim. The server wrote a sentence about this exact order and
            // paraphrasing it here would lose the one thing it knows and we do
            // not: what actually happened to it.
            message: e.message,
            allowed: detail.success ? (detail.data.allowed ?? null) : null,
            serverStatus: detail.success ? (detail.data.status ?? null) : null,
          });
        } else if (e instanceof Error) {
          setRefusal({
            orderId: order.order_id,
            message: e.message,
            allowed: null,
            serverStatus: null,
          });
        }
      } finally {
        setBusy(null);
        // Either way the row moved or did not; the server is the one that knows.
        orders.reload();
      }
    },
    [runtime, session, orders, prep, defaultPrep],
  );

  /**
   * One flag, one row, one tap — `POST /v1/menu/{items|modifiers}/{id}/sold-out`.
   * The board is re-read afterwards rather than patched in place, for the
   * pass's reason: the server's row is the one that is true, and the other
   * tablet may have tapped first.
   */
  const flip = useCallback(
    async (kind: 'items' | 'modifiers', id: string, soldOut: boolean): Promise<void> => {
      setFlipping(id);
      setBoardError(null);
      try {
        await runtime.api.request({
          method: 'POST',
          path: `/menu/${kind}/${id}/sold-out`,
          body: {
            sold_out: soldOut,
            ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
          },
          idempotencyKey: ulid(),
          schema: MenuSoldOutResult,
        });
      } catch (e) {
        if (e instanceof Error) setBoardError(e.message);
      } finally {
        setFlipping(null);
        menu.reload();
      }
    },
    [runtime, session, menu],
  );

  if (!entitled) {
    return (
      <section className="view">
        <p className="empty">{t('orders.notSwitchedOn')}</p>
      </section>
    );
  }

  return (
    <section className="view view--orders">
      <div className="view__header">
        <div className="date-picker">
          <button type="button" className="button" onClick={() => setPicked(addDays(day, -1))}>
            ‹
          </button>
          <input type="date" value={day} onChange={(e) => setPicked(e.target.value)} />
          <button type="button" className="button" onClick={() => setPicked(addDays(day, 1))}>
            ›
          </button>
        </div>
        <h1>{t('orders.title')}</h1>
        <span className="status-strip__staleness">
          {`${formatFreshness(orders.fetchedAt, now, timezone, locale)}${
            orders.error === null ? '' : t('orders.couldNotReach')
          }`}
        </span>
      </div>

      {orders.loading && orders.fetchedAt === null ? (
        <p className="empty">{t('orders.reading')}</p>
      ) : null}

      {!orders.loading && rows.length === 0 ? (
        <p className="empty">{t('orders.noneOn', { day })}</p>
      ) : (
        <div className="lanes">
          {LANES.map((lane) => {
            const inLane = live.filter((order) => order.status === lane.status);
            return (
              <section key={lane.status} className={`lane lane--${lane.status}`}>
                <h2 className="lane__head">
                  {t(lane.label)} <span className="lane__count">{inLane.length}</span>
                </h2>
                {inLane.length === 0 ? (
                  <p className="lane__empty muted">—</p>
                ) : (
                  <ul className="lane__list">
                    {inLane.map((order) => (
                      <li key={order.order_id} className="ticket">
                        <button
                          type="button"
                          className="ticket__open"
                          onClick={() => setOpenTicket(order.order_id)}
                        >
                          {/* The guest's own name, as the order carried it. */}
                          <strong>{order.client_name ?? t('orders.counter')}</strong>
                          <small>
                            {t('orders.itemsAndTotal', {
                              count: order.lines.reduce((sum, line) => sum + line.qty, 0),
                              total: formatMoney(order.total_cents, currency, locale),
                            })}
                          </small>
                          {order.pickup_at === null ? null : (
                            <small className="ticket__pickup">
                              {t('orders.pickup', {
                                clock: formatClock(order.pickup_at, timezone, locale),
                              })}
                            </small>
                          )}
                          {order.outstanding_cents > 0 ? (
                            <small className="muted">
                              {t('orders.toCollect', {
                                amount: formatMoney(order.outstanding_cents, currency, locale),
                              })}
                            </small>
                          ) : (
                            <small className="muted">{t('orders.paid')}</small>
                          )}
                        </button>

                        {refusal !== null && refusal.orderId === order.order_id ? (
                          <p className="error ticket__refusal" role="alert">
                            {refusal.message}
                          </p>
                        ) : null}

                        {order.prep_minutes === null ? null : (
                          <small className="muted ticket__prep">
                            {t('orders.prepTail', { minutes: order.prep_minutes })}
                          </small>
                        )}

                        <div className="row-actions">
                          {/*
                            The minutes box, beside Confirm and only there: a
                            prep time is written on that one move (0057) and
                            a box on a `ready` ticket would be a number with
                            no subject. Prefilled from the house default; an
                            empty box sends nothing and gets the same default.
                          */}
                          {buttonsFor(order, refusal).includes('confirmed') ? (
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              max={720}
                              className="ticket__prep-input"
                              aria-label={t('orders.prepMinutes')}
                              placeholder={t('orders.prepPlaceholder')}
                              value={prepFor(order.order_id)}
                              disabled={busy === order.order_id}
                              onChange={(e) =>
                                setPrep((held) => ({ ...held, [order.order_id]: e.target.value }))
                              }
                            />
                          ) : null}
                          {buttonsFor(order, refusal).map((status) => (
                            <button
                              key={status}
                              type="button"
                              className={`link-button${status === 'cancelled' ? ' link-button--danger' : ''}`}
                              disabled={busy === order.order_id}
                              onClick={() => void move(order, status)}
                            >
                              {t(STATUS_VERB[status])}
                            </button>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {cancelled.length === 0 ? null : (
        <article className="panel panel--muted">
          <h2>{t('orders.cancelledHeading')}</h2>
          <ul className="lane__list lane__list--cancelled">
            {cancelled.map((order) => (
              <li key={order.order_id} className="ticket ticket--cancelled">
                <button
                  type="button"
                  className="ticket__open"
                  onClick={() => setOpenTicket(order.order_id)}
                >
                  <strong>{order.client_name ?? t('orders.counter')}</strong>
                  <small>
                    {t('orders.cancelledSummary', {
                      total: formatMoney(order.total_cents, currency, locale),
                      clock: formatClock(order.placed_at, timezone, locale),
                    })}
                  </small>
                </button>
              </li>
            ))}
          </ul>
        </article>
      )}

      <Board
        items={menu.data?.items ?? []}
        loading={menu.loading && menu.fetchedAt === null}
        open={boardOpen}
        onToggle={() => setBoardOpen((was) => !was)}
        mayWrite={mayWrite}
        flipping={flipping}
        error={boardError}
        onFlip={(kind, id, soldOut) => void flip(kind, id, soldOut)}
        t={t}
      />

      {ticket === null ? null : (
        <TicketSheet
          order={ticket}
          currency={currency}
          timezone={timezone}
          t={t}
          locale={locale}
          onClose={() => setOpenTicket(null)}
        />
      )}
    </section>
  );
}

/**
 * **The 86 board** — every dish and every choice, with one tap each.
 *
 * Folded by default: a pass is a wall of tickets and the board is the thing a
 * cook opens when the snapper runs out, not a panel that competes with the
 * lanes for the eye. The dishes are grouped by the menu's own category, in
 * the menu's own order (`listMenuItems` sorts by `(sort_order, id)` at every
 * level), because that is the order the cook knows them in.
 *
 * A retired dish (`active: false`) is shown as retired and gets no flag: the
 * 86 is about tonight, and "we stopped serving it" is a catalog edit made
 * somewhere else on a different day.
 */
function Board({
  items,
  loading,
  open,
  onToggle,
  mayWrite,
  flipping,
  error,
  onFlip,
  t,
}: {
  items: readonly StaffMenuItem[];
  loading: boolean;
  open: boolean;
  onToggle: () => void;
  mayWrite: boolean;
  flipping: string | null;
  error: string | null;
  onFlip: (kind: 'items' | 'modifiers', id: string, soldOut: boolean) => void;
  t: T;
}): ReactNode {
  const out = items.filter((item) => item.active && item.sold_out).length;
  const categories: { name: string; items: StaffMenuItem[] }[] = [];
  for (const item of items) {
    const last = categories[categories.length - 1];
    if (last !== undefined && last.name === item.category) last.items.push(item);
    else categories.push({ name: item.category, items: [item] });
  }

  return (
    <article className="panel panel--board">
      <div className="panel__head">
        <h2>
          {t('orders.boardTitle')} {out === 0 ? null : <span className="lane__count">{out}</span>}
        </h2>
        <button type="button" className="link-button" onClick={onToggle} aria-expanded={open}>
          {open ? t('orders.boardHide') : t('orders.boardShow')}
        </button>
      </div>
      {!open ? null : loading ? (
        <p className="empty">{t('orders.boardReading')}</p>
      ) : items.length === 0 ? (
        <p className="empty">{t('orders.boardEmpty')}</p>
      ) : (
        <>
          <p className="muted">{t('orders.boardNote')}</p>
          {error === null ? null : (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {categories.map((category) => (
            <section key={category.name} className="board__category">
              <h3>{category.name}</h3>
              <ul className="board__list">
                {category.items.map((item) => (
                  <li
                    key={item.item_id}
                    className={`board__dish${item.sold_out ? ' board__dish--out' : ''}`}
                  >
                    <div className="board__row">
                      <strong>{item.name}</strong>
                      {!item.active ? (
                        <small className="muted">{t('orders.boardRetired')}</small>
                      ) : item.sold_out ? (
                        <small className="board__flag">{t('orders.soldOut')}</small>
                      ) : null}
                      {mayWrite && item.active ? (
                        <button
                          type="button"
                          className={`link-button${item.sold_out ? '' : ' link-button--danger'}`}
                          disabled={flipping === item.item_id}
                          onClick={() => onFlip('items', item.item_id, !item.sold_out)}
                        >
                          {item.sold_out ? t('orders.backOn') : t('orders.eightySix')}
                        </button>
                      ) : null}
                    </div>
                    {item.groups.some((group) => group.modifiers.length > 0) ? (
                      <ul className="board__choices">
                        {item.groups.flatMap((group) =>
                          group.modifiers.map((modifier) => (
                            <li
                              key={modifier.modifier_id}
                              className={`board__choice${modifier.sold_out ? ' board__choice--out' : ''}`}
                            >
                              <span>
                                {modifier.name}
                                {modifier.sold_out ? (
                                  <small className="board__flag">{t('orders.soldOut')}</small>
                                ) : null}
                              </span>
                              {mayWrite && item.active ? (
                                <button
                                  type="button"
                                  className="link-button"
                                  disabled={flipping === modifier.modifier_id}
                                  aria-label={t('orders.flipChoice', {
                                    name: modifier.name,
                                    verb: modifier.sold_out
                                      ? t('orders.backOn')
                                      : t('orders.eightySix'),
                                  })}
                                  onClick={() =>
                                    onFlip('modifiers', modifier.modifier_id, !modifier.sold_out)
                                  }
                                >
                                  {modifier.sold_out ? t('orders.backOn') : t('orders.eightySix')}
                                </button>
                              ) : null}
                            </li>
                          )),
                        )}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </article>
  );
}

/**
 * Which buttons this ticket gets.
 *
 * `next_statuses` normally, and `allowed` from the refusal when the server has
 * just told us this row is not what we thought — for the few hundred
 * milliseconds before the re-read lands, the error is fresher than the list we
 * are holding, and it is the server's list either way.
 */
function buttonsFor(order: OrderType, refusal: Refusal | null): OrderStatus[] {
  if (
    refusal !== null &&
    refusal.orderId === order.order_id &&
    refusal.allowed !== null &&
    order.status !== refusal.serverStatus
  ) {
    return refusal.allowed;
  }
  // A merch order shares the table and its next statuses are the fulfilled
  // lane's — written by machines, never by a kitchen. Only the six verbs this
  // board owns become buttons; the rest are the Parcels board's business.
  return order.next_statuses.filter((status): status is OrderStatus =>
    OrderStatus.options.includes(status as OrderStatus),
  );
}

function TicketSheet({
  order,
  currency,
  timezone,
  t,
  locale,
  onClose,
}: {
  order: OrderType;
  currency: string;
  timezone: string;
  t: T;
  locale: UiLocale;
  onClose: () => void;
}): ReactNode {
  return (
    <div className="sheet" role="dialog" aria-label={t('orders.dialogLabel')}>
      <div className="sheet__panel">
        <h2>{order.client_name ?? t('orders.counterOrder')}</h2>
        {/* `order.status` is the domain's own word, printed as it came. */}
        <p className="muted">
          {t('orders.placedAt', { clock: formatClock(order.placed_at, timezone, locale) })}
          {order.pickup_at === null
            ? ''
            : t('orders.pickupTail', {
                clock: formatClock(order.pickup_at, timezone, locale),
              })}
          {order.prep_minutes === null ? '' : t('orders.prepTail', { minutes: order.prep_minutes })}{' '}
          · {order.status}
        </p>

        <ul className="checkout-lines">
          {order.lines.map((line) => (
            <li key={line.line_id}>
              <span className="checkout-lines__qty">{t('orders.qty', { qty: line.qty })}</span>
              <span className="checkout-lines__what">
                {/* `item_name`, the size and modifier names are the tenant's menu. */}
                <strong>
                  {line.option_name === null
                    ? line.item_name
                    : t('orders.sized', { name: line.item_name, size: line.option_name })}
                </strong>
                {line.modifiers.length === 0 ? null : (
                  <small className="muted">
                    {line.modifiers
                      .map((modifier) =>
                        modifier.price_delta_cents === 0
                          ? modifier.name
                          : t('orders.modifierWithPrice', {
                              name: modifier.name,
                              amount: formatMoney(modifier.price_delta_cents, currency, locale),
                            }),
                      )
                      .join(' · ')}
                  </small>
                )}
                {/*
                  The guest's own words about this dish (migration 0034), under
                  the dish rather than at the foot of the ticket — which is the
                  whole reason the column exists: a ticket-wide "no onions" on
                  four dishes is a sentence the cook has to guess the subject
                  of. It is marked rather than muted, because it is the line on
                  a ticket that changes what the kitchen does.
                */}
                {line.notes === '' ? null : (
                  <small className="checkout-lines__note">{line.notes}</small>
                )}
              </span>
              <span className="checkout-lines__price">
                {formatMoney(line.line_total_cents, currency, locale)}
              </span>
            </li>
          ))}
        </ul>

        {order.notes === null || order.notes === '' ? null : <p className="note">{order.notes}</p>}

        <div className="checkout-total">
          <span className="checkout-total__sum">
            {formatMoney(order.total_cents, currency, locale)}
          </span>
          <small className="muted">
            {order.prepaid_cents > 0
              ? t('orders.alreadyPaid', {
                  amount: formatMoney(order.prepaid_cents, currency, locale),
                })
              : ''}
            {order.outstanding_cents > 0
              ? t('orders.toCollect', {
                  amount: formatMoney(order.outstanding_cents, currency, locale),
                })
              : t('orders.paidInFull')}
          </small>
        </div>

        <div className="sheet__actions">
          <button type="button" className="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
